import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { clockTime, fullTime } from '../../../shared/lib/format';
import { Button } from '../../../shared/ui/button';
import { Claim } from '../../../shared/ui/claim';
import { PageBand, PageHeader } from '../../../shared/ui/layout';
import { MutationError } from '../../../shared/ui/mutation-error';
import { Note } from '../../../shared/ui/note';
import { StatusBadge } from '../../../shared/ui/status-badge';
import {
  flowIssueKind,
  previewIssueRedeliver,
  REDELIVERABLE,
  resolveIssue,
  type TIssueDetail,
} from '../../../entities/issue';
import { flowKeys, getFlow, getVersion, parseGraph } from '../../../entities/flow';
import { IssueActions } from '../../../features/issue-actions';
import { IssueRedeliver } from '../../../features/issue-redeliver';
import { NodeGlyph } from '../../../widgets/flow-canvas';
import { FailedCopies } from './failed-copies';
import { FlowPath } from './flow-path';
import { IssueHistogram } from './issue-histogram';
import { AiBudgetCard, MergeCard, RateLimitCard, RemovedCard, ScheduleCard } from './kind-card';

interface IFlowIssuePageProps {
  issue: TIssueDetail;
  flowId: string;
  timeZone?: string;
}

// Board 19: an issue keyed on one step of a flow — what the step is doing, what it rests on,
// where it sits, and the one thing to do about it.
export const FlowIssuePage = ({ issue, flowId, timeZone }: IFlowIssuePageProps) => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [redelivering, setRedelivering] = useState(false);
  const now = useMemo(() => Date.now(), [issue]);
  const kind = flowIssueKind(issue);
  const closed = issue.state === 'resolved' || issue.state === 'ignored';

  const flow = useQuery({ queryKey: flowKeys.one(flowId), queryFn: () => getFlow(flowId) });
  const liveVersion = flow.data?.liveVersion?.number ?? null;

  // A handled issue still offers the resend while copies it counted can go again: the error
  // branch may have carried them on, but the step itself never delivered them.
  const preview = useQuery({
    queryKey: ['issue-redeliver-preview', issue.id],
    queryFn: () => previewIssueRedeliver(issue),
    enabled: REDELIVERABLE[kind],
  });
  const toResend = preview.data?.total ?? null;
  const redeliverable = REDELIVERABLE[kind] && (!closed || (toResend ?? 0) > 0);

  const handled = useMutation({
    mutationFn: () => resolveIssue(issue.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['issue', issue.id] });
      void client.invalidateQueries({ queryKey: ['issues'] });
    },
  });

  // The destination is on the copies, not the issue.
  const copy = issue.flowRuns
    .flatMap((run) => run.copies)
    .find((each) => each.issueId === issue.id && each.destinationId);
  const destinationId = copy?.destinationId ?? null;
  const destinationName = copy?.destinationName ?? null;

  // A step gone from the live version is read from the version its copies ran on.
  const ranOn =
    issue.flowRuns.find((run) => run.versionNumber !== null)?.versionNumber ??
    issue.lastVersionWithNode;
  const graphVersion: number | 'live' = kind === 'removed' && ranOn !== null ? ranOn : 'live';
  const oldGraph = useQuery({
    queryKey: flowKeys.version(flowId, graphVersion),
    queryFn: () => getVersion(flowId, graphVersion),
    enabled: kind === 'removed',
  });
  const oldNode = parseGraph(oldGraph.data?.graph ?? null)?.nodes.find(
    (node) => node.key === issue.node?.key,
  );
  const step = issue.node
    ? {
        ...issue.node,
        name: oldNode?.name ?? issue.node.name,
        type: oldNode?.type ?? issue.node.type,
      }
    : null;
  const shownVersion = kind === 'removed' ? ranOn : liveVersion;
  const redeliverLabel =
    kind === 'removed'
      ? `Redeliver on v${ranOn}…`
      : toResend === null
        ? 'Redeliver…'
        : `Redeliver ${toResend.toLocaleString('en-US')}…`;
  // One filled button per screen: for a 429 the fix is the rate, so the resend steps back; a
  // 4xx was refused as sent, and the same body gets the same answer until something is fixed.
  const refused = issue.errorClass === 'status_4xx' || issue.errorClass === 'auth_failed';
  const redeliverPrimary = kind === 'failure' && !refused;

  return (
    <>
      <PageHeader
        width="wide"
        title={issue.title}
        breadcrumbs={
          <Link to="/issues" className="text-muted hover:text-primary">
            Issues
          </Link>
        }
        badge={
          <span className="flex items-center gap-2 text-meta text-muted">
            <StatusBadge vocabulary="issue" value={issue.state} />
            <span title={fullTime(issue.firstSeen, timeZone)}>
              since {clockTime(issue.firstSeen, timeZone)}
            </span>
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            {step && (
              <span className="flex items-center gap-2 text-primary">
                <NodeGlyph node={{ key: step.key, type: step.type, name: step.name, config: {} }} />
                {step.name}
              </span>
            )}
            <span className="font-mono text-micro text-faint">in</span>
            <Link to="/flows/$flowId" params={{ flowId }} className="text-primary underline">
              {issue.flowName ?? flow.data?.name ?? 'its flow'}
            </Link>
            {shownVersion !== null && (
              <span className="font-mono text-micro text-faint">v{shownVersion}</span>
            )}
          </span>
        }
        actions={
          <>
            <IssueActions issue={issue} closeInMenu={false} timeZone={timeZone} />
            {!closed && (
              <Button
                size="sm"
                variant="ghost"
                waiting={handled.isPending}
                waitingLabel="Marking"
                onClick={() => handled.mutate()}
              >
                Mark handled
              </Button>
            )}
            {refused && !closed && issue.node && (
              <Button
                size="sm"
                onClick={() =>
                  void navigate({
                    to: '/flows/$flowId',
                    params: { flowId },
                    search: { node: issue.node?.key },
                  })
                }
              >
                Fix the step
              </Button>
            )}
            {redeliverable && toResend !== 0 && kind !== 'removed' && (
              <Button
                size="sm"
                variant={redeliverPrimary ? 'primary' : refused ? 'ghost' : 'secondary'}
                title={
                  refused
                    ? 'Refused as sent (4xx): fix the step or the endpoint first, or these fail again'
                    : undefined
                }
                onClick={() => setRedelivering(true)}
              >
                {redeliverLabel}
              </Button>
            )}
          </>
        }
      />

      <PageBand width="wide" className="flex flex-col gap-5 py-5">
        {handled.isError && <MutationError error={handled.error} />}
        {refused && !closed && (
          <Note>
            {destinationName ?? 'The destination'} refused these as sent. Resending the same body
            gets the same answer: fix the step or the endpoint first — Redeliver stays beside Fix
            the step for when it is fixed.
          </Note>
        )}
        <Claim
          basis={
            issue.evidence.length > 0
              ? issue.evidence.map((line) => (
                  <span key={line} className="flex gap-2">
                    <span aria-hidden className="mt-2 size-1 shrink-0 rounded-full bg-step" />
                    <span className="text-data leading-20 text-muted">{line}</span>
                  </span>
                ))
              : undefined
          }
        >
          {issue.conclusion ?? issue.title}
        </Claim>

        {kind === 'rate' && (
          <RateLimitCard
            issue={issue}
            destinationId={destinationId}
            destinationName={destinationName}
          />
        )}
        {kind === 'merge' && <MergeCard issue={issue} flowId={flowId} />}
        {kind === 'schedule' && (
          <ScheduleCard issue={issue} flowId={flowId} timeZone={timeZone} now={now} />
        )}
        {kind === 'ai_budget' && <AiBudgetCard issue={issue} timeZone={timeZone} />}
        {kind === 'removed' && (
          <RemovedCard
            issue={issue}
            ranOn={ranOn}
            onRedeliver={redeliverable && toResend !== 0 ? () => setRedelivering(true) : null}
          />
        )}

        <FlowPath issue={issue} flowId={flowId} versionNumber={graphVersion} />
        <IssueHistogram issue={issue} now={now} timeZone={timeZone} />
        <FailedCopies issue={issue} timeZone={timeZone} />
      </PageBand>

      {redeliverable && (
        <IssueRedeliver
          issue={issue}
          open={redelivering}
          onOpenChange={setRedelivering}
          rateLimited={kind === 'rate'}
          refused={refused}
          destinationName={destinationName}
          onVersion={kind === 'removed' ? ranOn : null}
        />
      )}
    </>
  );
};
